<template>
  <fts-design-modal
    append-to-body
    width="768px"
    desc="automation action designer"
    class="fts-automation-action-modal"
    ref="ftsDesignModal"
  >
    <!-- title -->
    <template #title>
      <div class="design-modal-header-title">{{ title }}</div>
    </template>
    <!-- body -->
    <template #body>
      <!-- spinner -->
      <div class="design-modal-spinner" v-if="loading">
        <fts-spinner show />
      </div>
      <div v-else-if="!selectedAction">
        <!-- selection -->
        <div class="selection-container">
          <!-- group -->
          <div
            class="selection-wrapper group fts-scrollbar fts-scrollbar-overlay"
          >
            <div
              v-for="actionGroup in groupSelections"
              :key="actionGroup.id"
              class="group-wrapper"
              :class="{
                active: selectedGroup && selectedGroup.id === actionGroup.id,
              }"
              @mouseenter="selectedGroup = actionGroup"
            >
              <div>{{ actionGroup.name }}</div>
            </div>
          </div>
          <!-- action -->
          <div
            class="selection-wrapper action fts-scrollbar fts-scrollbar-overlay"
          >
            <template v-if="selectedGroup && selectedGroup.id === 'basic'">
              <div
                v-for="action in actionSelections"
                :key="action.id"
                class="action-wrapper"
                @click="selectAction(action)"
              >
                <div class="action-label">{{ action.name }}</div>
                <div class="action-description">{{ action.description }}</div>
              </div>
            </template>
            <div class="action-empty" v-else>No available action.</div>
          </div>
        </div>
        <!-- error -->
        <div
          class="selection-error-message"
          v-if="$v.formData.type && $v.formData.type.$error"
        >
          To continue, please select an action.
        </div>
      </div>
      <!-- form -->
      <div v-else>
        <div v-for="field in selectedAction.form" :key="field.id">
          <!-- field -->
          <div v-if="field.type === 'field'">
            <fts-form-element
              :form-data="formData"
              :item="{
                type: 32,
                displayName: field.name,
                fieldName: field.id,
                required: field.required,
              }"
              :validator="$v.formData"
              comfort-design
            >
              <fts-formula
                slot="formElement"
                slot-scope="{ formData, item, onInput, onBlur }"
                v-model="formData[item.fieldName]"
                :variables="variables"
                @change="validateFormula"
                @input="onInput"
                @blur="onBlur"
              />
            </fts-form-element>
          </div>
          <!-- formula -->
          <div v-else-if="field.type === 'formula'">
            <div
              class="design-modal-label-group value-label-group"
              :class="{ multiple: formData[field.id + 'Expand'] }"
            >
              <div class="design-modal-label-group-l">
                <div class="fts-form-label">{{ field.name }}</div>
              </div>
              <div class="design-modal-label-group-r" v-if="field.expandable">
                <fts-btn
                  sm
                  outline
                  text-transform-init
                  @click="expandCollapseValue(field.id + 'Expand')"
                >
                  {{ formData[field.id + "Expand"] ? "Collapse" : "Expand" }}
                  Property
                </fts-btn>
              </div>
            </div>
            <!-- multiple -->
            <div
              class="value-property-container"
              v-if="formData[field.id + 'Expand']"
            >
              <fts-form-element
                :form-data="formData"
                :item="{
                  fieldName: 'fields',
                  label: false,
                }"
                :validator="$v.formData"
                comfort-design
              >
                <template #formElement="{ formData, item }">
                  <div
                    v-for="property in formData[item.fieldName]"
                    :key="property.id"
                    class="value-property-wrapper"
                  >
                    <!-- key -->
                    <div class="value-property-key">
                      <v-select
                        append-to-body
                        label="displayName"
                        :options="fieldOptions"
                        v-model="property.key"
                        :text="getName(property)"
                        :reduce="(x) => x.fieldName"
                        :filter="filterOptions"
                        :keys="fieldKeys"
                        comfort-design
                        @input="fieldValidatorOnInput(property.id, 'key')"
                        @search:blur="fieldValidatorOnInput(property.id, 'key')"
                      >
                        <div
                          class="field-select-option"
                          slot="selected-option"
                          slot-scope="{ text }"
                        >
                          <i
                            class="field-select-option__icon"
                            :class="
                              getIconByFieldName(property.key, fieldOptions)
                            "
                          />
                          <div class="field-select-option__label">
                            {{ text }}
                          </div>
                        </div>
                        <div
                          class="field-select-option"
                          slot="option"
                          slot-scope="option"
                        >
                          <i
                            class="field-select-option__icon"
                            :class="informationIcon(option.type)"
                          />
                          <div class="field-select-option__label">
                            {{ option.displayName }}
                          </div>
                        </div>
                      </v-select>
                      <div
                        class="design-modal-form-error"
                        v-if="fieldValidatorInvalid(property.id, 'key')"
                      >
                        Field is required.
                      </div>
                    </div>
                    <!-- value -->
                    <fts-formula
                      v-model="property.value"
                      :variables="variables"
                      class="value-property-value"
                      @change="validateFormula"
                    />
                    <!-- action -->
                    <fts-btn icon text @click="removeValue(property)">
                      <font-awesome-icon :icon="['far', 'trash-alt']" />
                    </fts-btn>
                  </div>
                  <!-- action add -->
                  <fts-btn
                    sm
                    outline
                    color="primary"
                    text-transform-init
                    @click="addValue"
                  >
                    <font-awesome-icon :icon="['fas', 'plus']" /> Add Value
                  </fts-btn>
                </template>
              </fts-form-element>
            </div>
            <!-- single -->
            <div v-else>
              <fts-form-element
                :form-data="formData"
                :item="{
                  fieldName: field.id,
                  required: field.required,
                  label: false,
                }"
                :validator="$v.formData"
                comfort-design
              >
                <fts-formula
                  slot="formElement"
                  slot-scope="{ formData, item, onInput, onBlur }"
                  v-model="formData[item.fieldName]"
                  :variables="variables"
                  @change="validateFormula"
                  @input="onInput"
                  @blur="onBlur"
                />
              </fts-form-element>
            </div>
          </div>
          <!-- module -->
          <div v-else-if="field.type == 'module'">
            <fts-form-element
              :form-data="formData"
              :item="{
                type: 27,
                displayName: 'Module',
                fieldName: 'module',
                required: field.required,
              }"
              :validator="$v.formData"
              comfort-design
            >
              <fts-select
                slot="formElement"
                slot-scope="{ formData, item, onInput, onBlur }"
                :form-data="formData"
                :item="item"
                :options="moduleOptions"
                comfort-design
                @input="onInput"
                @search:blur="onBlur"
                @optionSelected="updateModule"
                @clearSelection="updateModule"
              >
                <fts-label-group
                  slot="selected-option-container"
                  slot-scope="{ option }"
                  :item-id="option.id"
                  :list="moduleOptions"
                  emoji
                />
                <fts-label-group
                  slot="option"
                  slot-scope="{ option }"
                  :item-id="option.id"
                  :list="moduleOptions"
                  emoji
                />
              </fts-select>
            </fts-form-element>
          </div>
          <!-- acitivity -->
          <div v-else-if="field.type == 'activity'">
            <!-- select -->
            <fts-form-element
              :form-data="formData"
              :item="{
                type: 27,
                displayName: 'Activity',
                fieldName: field.id,
                required: field.required,
              }"
              :validator="$v.formData"
              v-if="selectedModule"
              comfort-design
            >
              <fts-select
                slot="formElement"
                slot-scope="{ formData, item, onInput, onBlur }"
                :form-data="formData"
                :item="item"
                :options="activityOptions"
                comfort-design
                @input="onInput"
                @search:blur="onBlur"
                @optionSelected="updateActivity"
                @clearSelection="updateActivity"
              >
                <fts-label-group
                  slot="selected-option-container"
                  slot-scope="{ option }"
                  :icon="getStandardActivityIcon(option.id)"
                  :item-id="option.id"
                  :list="activityOptions"
                  :emoji="!getStandardActivityIcon(option.id)"
                  system-default
                />
                <fts-label-group
                  slot="option"
                  slot-scope="{ option }"
                  :icon="getStandardActivityIcon(option.id)"
                  :item-id="option.id"
                  :list="activityOptions"
                  :emoji="!getStandardActivityIcon(option.id)"
                  system-default
                />
              </fts-select>
            </fts-form-element>
            <!-- formula -->
            <fts-form-element
              :form-data="formData"
              :item="{
                type: 32,
                fieldName: 'entry',
                required: selectedActivity && selectedActivity.id != 'create',
              }"
              :validator="$v.formData"
              comfort-design
              v-if="selectedActivity && selectedActivity.id != 'create'"
            >
              <fts-formula
                slot="formElement"
                slot-scope="{ formData, item, onInput, onBlur }"
                v-model="formData[item.fieldName]"
                :variables="variables"
                @change="validateFormula"
                @input="onInput"
                @blur="onBlur"
              />
            </fts-form-element>
          </div>
          <!-- form -->
          <div v-else-if="field.type == 'form'">
            <template v-if="selectedActivity && selectedActivity.hasForm">
              <div
                class="design-modal-label-group value-label-group"
                :class="{ multiple: formData[field.id + 'Expand'] }"
              >
                <div class="design-modal-label-group-l">
                  <div class="fts-form-label">{{ field.name }}</div>
                </div>
                <div class="design-modal-label-group-r" v-if="field.expandable">
                  <fts-btn
                    sm
                    outline
                    text-transform-init
                    @click="expandCollapseValue(field.id + 'Expand')"
                  >
                    {{ formData[field.id + "Expand"] ? "Collapse" : "Expand" }}
                    Property
                  </fts-btn>
                </div>
              </div>
              <!-- multiple -->
              <div
                class="value-property-container"
                v-if="formData[field.id + 'Expand']"
              >
                <fts-form-element
                  :form-data="formData"
                  :item="{
                    fieldName: 'fields',
                    label: false,
                  }"
                  :validator="$v.formData"
                  comfort-design
                >
                  <template #formElement="{ formData, item }">
                    <div
                      v-for="property in formData[item.fieldName]"
                      :key="property.id"
                      class="value-property-wrapper"
                    >
                      <!-- key -->
                      <div class="value-property-key">
                        <v-select
                          append-to-body
                          label="displayName"
                          :options="fieldOptions"
                          v-model="property.key"
                          :text="getName(property)"
                          :reduce="(x) => x.fieldName"
                          :filter="filterOptions"
                          :keys="fieldKeys"
                          comfort-design
                          @input="fieldValidatorOnInput(property.id, 'key')"
                          @search:blur="
                            fieldValidatorOnInput(property.id, 'key')
                          "
                        >
                          <div
                            class="field-select-option"
                            slot="selected-option"
                            slot-scope="{ text }"
                          >
                            <i
                              class="field-select-option__icon"
                              :class="
                                getIconByFieldName(property.key, fieldOptions)
                              "
                            />
                            <div class="field-select-option__label">
                              {{ text }}
                            </div>
                          </div>
                          <div
                            class="field-select-option"
                            slot="option"
                            slot-scope="option"
                          >
                            <i
                              class="field-select-option__icon"
                              :class="informationIcon(option.type)"
                            />
                            <div class="field-select-option__label">
                              {{ option.displayName }}
                            </div>
                          </div>
                        </v-select>
                        <div
                          class="design-modal-form-error"
                          v-if="fieldValidatorInvalid(property.id, 'key')"
                        >
                          Field is required.
                        </div>
                      </div>
                      <!-- value -->
                      <fts-formula
                        v-model="property.value"
                        :variables="variables"
                        class="value-property-value"
                        @change="validateFormula"
                      />
                      <!-- action -->
                      <fts-btn icon text @click="removeValue(property)">
                        <font-awesome-icon :icon="['far', 'trash-alt']" />
                      </fts-btn>
                    </div>
                    <!-- action add -->
                    <fts-btn
                      sm
                      outline
                      color="primary"
                      text-transform-init
                      @click="addValue"
                    >
                      <font-awesome-icon :icon="['fas', 'plus']" /> Add Value
                    </fts-btn>
                  </template>
                </fts-form-element>
              </div>
              <!-- single -->
              <div v-else>
                <fts-form-element
                  :form-data="formData"
                  :item="{
                    fieldName: field.id,
                    required: field.required,
                    label: false,
                  }"
                  :validator="$v.formData"
                  comfort-design
                >
                  <fts-formula
                    slot="formElement"
                    slot-scope="{ formData, item, onInput, onBlur }"
                    v-model="formData[item.fieldName]"
                    :variables="variables"
                    @change="validateFormula"
                    @input="onInput"
                    @blur="onBlur"
                  />
                </fts-form-element>
              </div>
            </template>
          </div>
          <!-- filter -->
          <div
            class="design-modal-form-group"
            v-else-if="field.type == 'filter' && selectedModule"
          >
            <div class="design-modal-form-label">Filter</div>
            <fts-query-designer
              :filter="formData.filter"
              :states="selectedModule.states"
              :information-source="querySelection"
              :variables="variables"
            />
          </div>
          <!-- contenteditable -->
          <div v-else-if="field.type == 'contenteditable'">
            <fts-form-element
              :form-data="formData"
              :item="{
                fieldName: field.id,
                required: field.required,
                label: false,
              }"
              :validator="$v.formData"
              comfort-design
            >
              <div
                class="action-variable"
                slot="formElement"
                slot-scope="{ formData, item, onInput, onBlur }"
              >
                <i
                  class="action-variable-icon"
                  :class="informationIcon(formData.variableType)"
                  v-if="selectedAction.variable"
                />

                <div class="action-variable-wrapper">
                  <fts-automation-contenteditable
                    v-model="formData[item.fieldName]"
                    class="action-variable-control"
                    @input="onInput"
                    @blur="onBlur"
                  />
                  <div
                    class="action-variable-tag"
                    v-if="
                      formData.moduleId ||
                      formData.activityId ||
                      formData.informationId
                    "
                  >
                    <fts-automation-badge
                      :module-id="formData.moduleId"
                      :activity-id="formData.activityId"
                      :informationId="formData.informationId"
                      :isList="formData.isList"
                      :isGroup="formData.isGroup"
                      class="action-variable-tag__badge"
                    />
                  </div>
                </div>
              </div>
            </fts-form-element>
          </div>
        </div>
        <!-- <fts-automation-scoped-variables :variables="variables" /> -->
        <!-- error message -->
        <div class="error-message" v-if="errorMessage">{{ errorMessage }}</div>
      </div>
    </template>
    <!-- footer -->
    <template #footerLeft>
      <fts-btn text icon @click="remove">
        <font-awesome-icon :icon="['far', 'trash-alt']" v-if="isExist" />
      </fts-btn>
    </template>
    <template #footerRight>
      <fts-btn text @click="beforeClose">Cancel</fts-btn>

      <fts-btn outline color="primary" @click="done">
        <font-awesome-icon :icon="['fas', 'check']" /> Ok
      </fts-btn>
    </template>
  </fts-design-modal>
</template>

<script>
import Vue from "vue";
import Vuelidate from "vuelidate";
import { required, requiredIf } from "vuelidate/lib/validators";
Vue.use(Vuelidate);

import changesets from "diff-json";
import vSelect from "../../vue-select";

import commons from "../../commons/commons";
import queryLogic from "../../commons/queryLogic";
import { automationDesignStore } from "../../store/automationDesignStore";

import FTSDesignModal from "../FTSDesignModal";
import FTSBtn from "../../elements/FTSBtn";
import FTSFormElement from "../../elements/FTSFormElement";
import FTSSelect from "../../elements/FTSSelect";
import FTSLabelGroup from "../../elements/display/FTSLabelGroup";
import automationBlocks from "../../commons/automationBlocks";
import FTSFormula from "../FTSFormula";
import FTSQueryDesigner from "../../design/FTSQueryDesigner";
import FTSAutomationScopedVariables from "./FTSAutomationScopedVariables";
import FTSAutomationContenteditable from "./FTSAutomationContenteditable";
import FTSAutomationBadge from "./FTSAutomationBadge";
import FTSSpinner from "../../elements/FTSSpinner";

export default {
  components: {
    "fts-design-modal": FTSDesignModal,
    "fts-btn": FTSBtn,
    "fts-form-element": FTSFormElement,
    "fts-select": FTSSelect,
    "fts-label-group": FTSLabelGroup,
    "fts-formula": FTSFormula,
    "fts-query-designer": FTSQueryDesigner,
    "fts-automation-scoped-variables": FTSAutomationScopedVariables,
    "fts-automation-contenteditable": FTSAutomationContenteditable,
    "fts-automation-badge": FTSAutomationBadge,
    "fts-spinner": FTSSpinner,
    "v-select": vSelect,
  },
  data: () => ({
    nodeData: null,
    formData: null,
    originalData: null,
    resolve: null,
    states: [],
    selectedGroup: null,
    selection: [],
    selectedAction: null,
    information: [],
    content: null,
    variables: [],
    moduleOptions: [],
    selectedModule: null,
    selectedActivity: null,
    classification: [],
    lastVariable: null,
    originalFieldOptions: [],
    fieldOptions: [],
    errorMessage: null,
    loading: false,
  }),
  validations() {
    let formData = {};
    formData = {
      type: { required },
    };

    if (this.selectedAction && Array.isArray(this.selectedAction.form)) {
      this.selectedAction.form.forEach((x) => {
        /* [type: formula] */
        if (x.type === "formula") {
          formData[x.id] = {
            required: requiredIf((y) => !y[`${x.id}Expand`]),
          };
          formData.fields = {
            required: requiredIf((y) => y[`${x.id}Expand`]),
            $each: {
              key: {
                required: requiredIf(() => this.formData[`${x.id}Expand`]),
              },
              $trackBy: "id",
            },
          };
        } else if (x.type === "form") {
          /* [type: form] */
          const hasForm =
            this.selectedActivity && this.selectedActivity.hasForm;
          formData[x.id] = {
            required: requiredIf((y) => hasForm && !y[`${x.id}Expand`]),
          };
          formData.fields = {
            required: requiredIf((y) => hasForm && y[`${x.id}Expand`]),
            $each: {
              key: {
                required: requiredIf(
                  () => hasForm && this.formData[`${x.id}Expand`]
                ),
              },
              $trackBy: "id",
            },
          };
        } else if (x.required) {
          formData[x.id] = { required };
        }
      });
    }
    /* [type: module] moduleId */
    if (formData.moduleId) {
      formData.module = formData.moduleId;
    }
    /* [type: activity] entry */
    if (formData.activityId) {
      formData.entry = {
        required: requiredIf((x) => x.activityId != "create"),
      };
    }
    return { formData };
  },
  methods: {
    async show(item, variables, nodeData) {
      this.nodeData = nodeData;
      this.variables = variables;
      this.selectedAction = null;
      this.selectedModule = null;
      this.formData = _.cloneDeep(item);
      this.loading = true;

      if (this.formData.type) {
        this.selectedAction = this.actionSelections.find(
          (x) => x.id === this.formData.type
        );
      } else {
        this.selectedGroup = this.groupSelections[0];
      }

      return new Promise(async (resolve) => {
        this.resolve = resolve;
        this.$refs.ftsDesignModal.show({
          beforeCloseHandler: () => {
            const diff = changesets.diff(this.formData, this.originalData);
            if (diff && diff.length) {
              this.$modal.show("dialog", {
                title: "Confirm discard changes",
                text: "Discard all changes?",
                buttons: [
                  { title: "Cancel" },
                  {
                    title: "Discard",
                    default: true,
                    handler: () => {
                      this.$modal.hide("dialog");
                      this.reset();
                    },
                  },
                ],
              });
            } else {
              this.reset();
            }
          },
        });

        if (
          this.formData.module &&
          Object.prototype.toString.call(this.formData.module) === "[object String]"
        ) {
          this.formData.module = parseInt(this.formData.module);
        }

        if (this.selectedAction) {
          if (this.selectedAction.variable && !this.formData.fieldName) {
            /* set fieldName */
            this.formData.fieldName = commons.generateFieldName();
          }

          if (
            this.selectedAction.id == "find" ||
            this.selectedAction.id == "findAll" ||
            this.selectedAction.id == "getForm" ||
            this.selectedAction.id == "doActivity"
          ) {
            await this.loadModule();
            /* set selectedModule */
            this.selectedModule = this.moduleOptions.find(
              (x) => x.id == this.formData.module
            );
            /* set filter */
            if (
              (this.selectedAction.id == "find" || this.selectedAction.id == "findAll") &&
              !this.formData.filter
            ) {
              Vue.set(this.formData, "filter", {
                items: [],
              });
            }
          }

          if (this.selectedAction.id == "assign") {
            /* set fieldOptions */
            this.fieldOptions = await this.prepareFieldOptions();
          }

          if (this.selectedAction.id == "getForm" || this.selectedAction.id == "doActivity") {
            /* set selectedActivity */
            this.selectedActivity = this.activityOptions.find(
              (x) => x.id == this.formData.activityId
            );
            /* set fieldOptions */
            if (this.selectedModule && this.selectedActivity)
              this.fieldOptions = await this.prepareFieldOptions();
          }
        }

        this.loading = false;
        this.$nextTick(() => {
          this.originalData = _.cloneDeep(this.formData);
        });
      });
    },
    done() {
      /* set type */
      this.formData.type = this.selectedAction && this.selectedAction.id;
      /* validations */
      this.$v.formData.$touch();
      if (!this.$v.formData.$invalid && !this.errorMessage) {
        if (this.formData) this.resolve && this.resolve(this.formData);
        this.reset();
      } else {
        console.info("Invalid form", this.$v.formData);
      }
    },
    beforeClose() {
      this.$refs.ftsDesignModal.beforeClose();
    },
    remove() {
      this.$modal.show("dialog", {
        title: "Confirmation",
        text: `Delete ${this.formData.name}?`,
        buttons: [
          { title: "Cancel" },
          {
            title: "Delete",
            default: true,
            handler: () => {
              this.$modal.hide("dialog");
              this.resolve && this.resolve(true);
              this.reset();
            },
          },
        ],
      });
    },
    reset() {
      this.$v.$reset();
      this.$refs.ftsDesignModal.close();
      Object.assign(this.$data, this.$options.data.apply(this));
    },
    informationIcon(id) {
      return commons.informationIcon(id) || "fts-icon-text";
    },
    getIconByFieldName(key, list) {
      const field = list.find((x) => x.fieldName === key);
      if (field) {
        return this.informationIcon(field.type);
      }
    },
    getName(property) {
      if (!this.fieldOptions) {
        return "";
      }

      let field = this.fieldOptions.find((x) => x.fieldName == property.key);
      if (field) return field.displayName;
      return null;
    },
    validateFormula: _.debounce(async function () {
      if (this.nodeData && this.nodeData.id) {
        this.errorMessage = null;
        let diagram = _.cloneDeep(automationDesignStore.state.diagram);
        let node = Object.values(diagram).find(
          (x) => x.data.id == this.nodeData.id
        );
        let action = node.data.actions.find((x) => x.id == this.formData.id);

        if (!action) {
          node.data.actions.push(this.formData);
          action = this.formData;
        } else {
          Object.assign(action, this.formData);
        }

        try {
          let result = await this.$store.dispatch("validateAutomation", {
            blocks: diagram,
            validate: {
              nodeId: this.nodeData.id,
              actionId: action.id,
            },
          });

          let returnedNode = Object.values(result.data.blocks).find(
            (x) => x.data.id == this.nodeData.id
          );
          let returnedAction = returnedNode.data.actions.find(
            (x) => x.id == this.formData.id
          );
          Vue.set(this.formData, "variableType", returnedAction.variableType);

          if (returnedAction.moduleId)
            Vue.set(this.formData, "moduleId", returnedAction.moduleId);
          else this.$delete(this.formData, "moduleId");

          if (returnedAction.activityId)
            Vue.set(this.formData, "activityId", returnedAction.activityId);
          else this.$delete(this.formData, "activityId");

          if (returnedAction.informationId)
            Vue.set(this.formData, "informationId", returnedAction.informationId);
          else this.$delete(this.formData, "informationId");

          if (returnedAction.isList)
            Vue.set(this.formData, "isList", returnedAction.isList);
          else {
            this.$delete(this.formData, "isList");
          }

          if (returnedAction.isGroup)
            Vue.set(this.formData, "isGroup", returnedAction.isGroup);
          else {
            this.$delete(this.formData, "isGroup");
          }

          if (this.formData.type == "assign") {
            if (
              returnedAction.variableType == 35 ||
              returnedAction.variableType == 36 ||
              returnedAction.variableType == 7
            ) {
              this.fieldOptions = await this.prepareFieldOptions();
            }
          }
        } catch (err) {
          if (err.response && err.response.data && err.response.data.message) {
            this.errorMessage = err.response.data.message;
          } else {
            this.errorMessage = "Something went wrong";
          }
        }
      }
    }, 500),
    async selectAction(action) {
      this.selectedAction = action;
      this.formData.type = action.id;
      this.loading = true;
      
      if (this.selectedAction) {
        if (this.selectedAction.variable && !this.formData.fieldName) {
          this.formData.fieldName = commons.generateFieldName();
        }

        if (
          this.selectedAction.id == "find" ||
          this.selectedAction.id == "findAll" ||
          this.selectedAction.id == "getForm" ||
          this.selectedAction.id == "doActivity"
        ) {
          // this.moduleOptions = this.$store.state.workspace.vectors;
          await this.loadModule();
      
          if (
            this.selectedAction.id == "find" ||
            this.selectedAction.id == "findAll"
          ) {
            Vue.set(this.formData, "variableType", 7);
            if (!this.formData.filter) {
              Vue.set(this.formData, "filter", {
                items: [],
              });
            }
          } else if (
            this.selectedAction.id == "getForm"
          ) {
            Vue.set(this.formData, "variableType", 35);
          }

          if (this.selectedAction.id == "findAll") {
            Vue.set(this.formData, "isList", true);
          }
        } else if (this.selectedAction.id == "setVariable") {
          if (!this.formData.name) {
            for (var i = 0; i < 10; i++) {
              if (!this.variables.find((x) => x.name == `Variable ${i + 1}`)) {
                this.formData.name = `Variable ${i + 1}`;
                break;
              }
            }
          }
        }
      }
      this.loading = false;
    },
    setLastVariable(variable) {
      this.lastVariable = variable;
    },
    updateModule(item) {
      if (item && item.id) {
        this.selectedModule = item;
        this.formData.moduleId = item.id.toString();
      } else {
        this.selectedModule = null;
        this.formData.moduleId = null;
      }
      /* reset activity */
      this.updateActivity();
    },
    updateActivity(item) {
      if (item && item.id) {
        this.selectedActivity = item;
        if (!this.formData.activityId)
          this.$set(this.formData, "activityId", item.id);
      } else {
        this.selectedActivity = null;
        if (this.formData.activityId) this.$delete(this.formData, "activityId");
      }
    },
    async expandCollapseValue(field) {
      if (!this.formData[field]) {
        this.$set(this.formData, field, true);
      } else {
        this.$delete(this.formData, field);
        // to-do clear formData.valueProperties?
      }

      if (this.formData[field] === true && !this.fieldOptions) {
        this.fieldOptions = await this.prepareFieldOptions();
      }
    },
    filterOptions(options, search) {
      let keys = this.formData.fields.map((x) => x.key);

      return options.filter(
        (x) =>
          !keys.some((y) => y == x.fieldName) &&
          x.displayName.toLowerCase().indexOf(search.toLowerCase()) > -1
      );
    },
    async updateClassification() {
      let result = await this.$store.dispatch("loadClassification", {
        moduleId: this.formData.module,
        activityId: this.formData.activityId,
      });
      this.classification = result.data;
    },
    async setFormProperty() {
      await this.updateClassification();
      if (!this.formData.enableValueProperty) {
        this.$set(this.formData, "enableValueProperty", true);
      } else {
        this.$delete(this.formData, "enableValueProperty");
        // to-do clear formData.valueProperties?
      }
    },
    addValue() {
      if (!this.formData.fields || !Array.isArray(this.formData.fields))
        this.$set(this.formData, "fields", []);

      this.formData.fields.push({
        id: commons.nanoID(),
      });
    },
    removeValue(item) {
      if (Array.isArray(this.formData.fields)) {
        const findIndex = this.formData.fields.findIndex(
          (x) => x.id === item.id
        );
        if (findIndex > -1) this.formData.fields.splice(findIndex, 1);
      }
    },
    async prepareFieldOptions() {
      let moduleId = this.formData.module;
      if (this.formData.moduleId) {
        moduleId = this.formData.moduleId;
      }

      if (moduleId) {
        let data = await this.$store.dispatch("findVariable", {
          moduleId: moduleId,
        });

        if (this.formData.informationId) {
          return data.information.find(
            (x) => x.id == this.formData.informationId
          ).fields;
        } else if (this.formData.activityId) {
          if (
            this.formData.activityId == "create" ||
            this.formData.activityId == "edit"
          ) {
            return data.standards.find((x) => x.id == this.formData.activityId)
              .classification;
          } else {
            return data.activities.find((x) => x.id == this.formData.activityId)
              .classification;
          }
        } else if (this.formData.informationId) {
          return data.information.find(
            (x) => x.id == this.formData.informationId
          ).fields;
        } else {
          return data.information;
        }
      } else if (this.selectedActivity) {
        let data = await this.$store.dispatch("findVariable", {
          moduleId: this.selectedModule.id,
        });

        if (
          this.selectedActivity.id == "create" ||
          this.selectedActivity.id == "edit"
        ) {
          return data.standards.find((x) => x.id == this.selectedActivity.id)
            .classification;
        } else {
          return data.activities.find((x) => x.id == this.selectedActivity.id)
            .classification;
        }
      }
    },
    /* module */
    async loadModule() {
      try {
        const res = await this.$store.dispatch("loadVectorSelection");
        this.moduleOptions = res.data;
      } catch(err) {
        console.error("AutomationAction:LoadModule", err);
      }
    },
    /* field validator */
    fieldValidator(rowId, fieldName) {
      try {
        const validations = Object.values(this.$v.formData.fields.$each.$iter);
        return validations.find((x) => x.$model.id === rowId)[fieldName];
      } catch (err) {
        console.warn("[FieldValidatorException]", err);
      }
    },
    fieldValidatorOnInput(rowId, fieldName) {
      let fieldValidator = this.fieldValidator(rowId, fieldName);
      return fieldValidator && fieldValidator.$touch();
    },
    fieldValidatorInvalid(rowId, fieldName) {
      const fieldValidator = this.fieldValidator(rowId, fieldName);
      return (
        fieldValidator &&
        fieldValidator.$invalid &&
        (fieldValidator.$dirty || this.$v.formData.$dirty)
      );
    },
    /* standard activity icon */
    getStandardActivityIcon(id) {
      if (['create', 'edit', 'delete'].indexOf(id) > -1)
        return commons.standardActivityIcon(id);
    },
  },
  computed: {
    fieldKeys() {
      return this.formData.fields ? this.formData.fields.map((x) => x.key) : [];
    },
    activityOptions() {
      let standards = [
        { id: "create", name: "Create", hasForm: true },
        { id: "edit", name: "Edit", hasForm: true },
        // { id: "changeStatus", name: "change state" },
        { id: "delete", name: "Delete" },
      ];

      if (this.selectedModule == null) return standards;

      if (this.formData.type == "doActivity") {
        return standards.concat(this.selectedModule.activities);
      }

      return standards
        .concat(this.selectedModule.activities)
        .filter((x) => x.hasForm);
    },
    title() {
      return !this.selectedAction
        ? "Select an action below"
        : (this.selectedAction && this.selectedAction.name) || "Action";
    },
    groupSelections() {
      return [
        { id: "basic", name: "Basic" },
        { id: "plugin", name: "Plugins" },
      ];
    },
    actionSelections() {
      return automationBlocks.actionSelections;
    },
    isExist() {
      if (this.nodeData && Array.isArray(this.nodeData.actions))
        return Boolean(this.nodeData.actions.find(x => x.id === this.formData.id));
    },
    querySelection() {
      return _.differenceWith(
        queryLogic.querySelectionPrefix,
        this.selectedModule.userCollection ? [] : ["username"],
        ({ id }, key) => id === key
      )
        .concat(this.selectedModule.information)
        .concat(queryLogic.querySelectionSuffix);
    },
  },
  watch: {
    "formData.module": function () {
      if (this.selectedModule) {
        if (this.formData.type == "find")
          this.formData.name = this.selectedModule.name + " Found";
        else if (this.formData.type == "findAll")
          this.formData.name = this.selectedModule.name + " List Found";
      }
    },
    "formData.activityId": async function () {
      if (this.selectedModule && this.selectedActivity) {
        if (this.formData.type == "getForm")
          this.formData.name =
            this.selectedModule.name +
            " " +
            this.selectedActivity.name +
            " " +
            "Form";
        else if (this.formData.type == "doActivity")
          this.formData.name =
            this.selectedModule.name +
            " " +
            this.selectedActivity.name +
            " " +
            "Submission";

        this.fieldOptions = await this.prepareFieldOptions();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "./src/assets/scss/vars.scss";

.fts-automation-action-modal {
  /* selection */
  .selection-container {
    $selectionBoxShadow: 0 0 4px 2px
        rgba(
          $color: $primaryColor,
          $alpha: 0.08,
        ),
      0 1px 2px 0
        rgba(
          $color: $primaryColor,
          $alpha: 0.08,
        );
    display: flex;
    align-items: flex-start;
    font-size: 0.875rem;
    .selection-wrapper {
      max-height: 500px;
      border-radius: 0.25rem;
      background: #fff;
      box-shadow: var(--fts-elevation-1);
      &.group {
        flex: 1 1 40%;
        max-width: 40%;
        padding: 0.75rem;
      }
      &.action {
        flex: 1 1 calc(60% - 1rem);
        max-width: calc(60% - 1rem);
        height: 500px;
        margin-left: 1rem;
        padding: 0.75rem;
      }
    }
    .group-wrapper {
      padding: 0.575rem 0.75rem;
      border-radius: 0.25rem;
      transition: 0.3s ease-in-out;
      &:not(:last-child) {
        margin-bottom: 0.575rem;
      }
      &:hover {
        box-shadow: $selectionBoxShadow;
      }
      &.active {
        color: $primaryColor;
        background: rgba($color: $primaryColor, $alpha: 0.075);
      }
    }
    .action-wrapper {
      padding: 0.575rem 0.75rem;
      background: #fff;
      border-radius: 0.25rem;
      transition: 0.3s ease-in-out;
      &:not(:last-child) {
        margin-bottom: 0.575rem;
      }
      &:hover {
        box-shadow: $selectionBoxShadow;
        color: $primaryColor;
        background: rgba($color: $primaryColor, $alpha: 0.075);
        cursor: pointer;
      }
      .action-label {
      }
      .action-description {
        margin-top: 0.175rem;
        font-size: 75%;
        color: $grayLightColor;
      }
    }
    .action-empty {
      padding: 0.75rem 1rem;
      text-align: center;
    }
  }
  .selection-error-message {
    margin-top: 1rem;
    font-size: 0.75rem;
    color: $dangerColor;
  }
  /* value label */
  .value-label-group {
    &:not(.multiple) {
      margin-bottom: 0.375rem;
    }
  }
  /* value property */
  .value-property-container {
    .value-property-wrapper {
      display: flex;
      align-items: flex-start;
      margin-bottom: 1.5rem;
      > * {
        &:not(:last-child) {
          margin-right: 0.75rem;
        }
      }
      .value-property-key {
        width: 280px;
      }
      .value-property-value {
        flex: 1;
      }
    }
  }
  /* variable */
  .action-variable {
    $variable-font-size: 1rem;
    $variable-icon-font-size: 0.875rem;
    display: flex;
    align-items: flex-start;
    .action-variable-icon {
      margin-top: calc(#{$variable-font-size - $variable-icon-font-size});
      margin-right: 0.375rem;
      font-size: $variable-icon-font-size;
    }
    .action-variable-wrapper {
      min-width: 0;
    }
    .action-variable-control {
      font-size: $variable-font-size;
    }
    .action-variable-tag {
      max-width: 100%;
      display: inline-block;
      margin-top: 0.175rem;
      padding: 0.175rem 0.575rem;
      border-radius: 0.5rem;
      border: 1px solid var(--fts-block-variable-border);
      background: var(--fts-block-variable-background);
      .action-variable-tag__badge {
        font-size: 0.675rem;
      }
    }
  }
  /* error message */
  .error-message {
    padding: 0.575rem 1rem;
    font-size: 0.875rem;
    border-radius: 0.25rem;
    background: rgba($color: $dangerColor, $alpha: 0.15);
    color: $dangerColor;
    line-height: 1.5;
  }
}
/* field select option */
.field-select-option {
  display: flex;
  align-items: center;
  .field-select-option__icon {
    margin-right: 0.375rem;
    color: $secondaryColor;
    &.field-select-option__icon--system {
      color: $primaryColor;
    }
  }
  .field-select-option__label {
    @include text-overflow-ellipsis;
  }
}
</style>